/**
 * Second Opinion — where the approved input lives on the device.
 *
 * `observer_invites` is the canonical record: the observation, the learner's
 * decision, the timestamps. This module holds only the approved payload, so
 * every surface that scores a run — the result page, the dossier, the
 * snapshot backfill, a replay from the response log — reads the same bounded
 * input without each of them needing a network round trip.
 *
 * Written through `cloudStorage`, so an approval given on a phone is present
 * on a laptop. Absent by default: a learner who never asked anyone, or asked
 * and binned it, is scored exactly as they were before this existed.
 */
import { cloudStorage } from "@/lib/cloudStore";
import type { ApprovedObserverInput } from "./observer";

const KEY = "wcc360.v2.observer_approved";

export function readApprovedObserverInput(): ApprovedObserverInput | undefined {
  try {
    const raw = cloudStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as ApprovedObserverInput;
    if (!parsed || !Array.isArray(parsed.passion_ids)) return undefined;
    // Defensive: an approval with nothing ticked is the same as no approval.
    if (parsed.passion_ids.length === 0 && (parsed.cause_ids?.length ?? 0) === 0) {
      return undefined;
    }
    return { ...parsed, cause_ids: parsed.cause_ids ?? [], note_ids: parsed.note_ids ?? [] };
  } catch {
    return undefined;
  }
}

export function writeApprovedObserverInput(input: ApprovedObserverInput | null): void {
  try {
    if (!input) cloudStorage.removeItem(KEY);
    else cloudStorage.setItem(KEY, JSON.stringify(input));
  } catch {
    /* a lost approval costs a nudge, never an answer */
  }
}
